import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useSiteContent } from "../../context/SiteContentContext";
import { Lines, thaiDate } from "../../components/site/siteText";
import { apiUrl, editionPdfUrl, getEdition } from "../../services/newspaperApi";
import { fileHref } from "../../services/apiClient";
import ArticleCredit from "./ArticleCredit";
import RichText, { plainText } from "../../components/site/RichText";
import PaperQr from "../../assests/images/qrcode.png";
import "./newspaper.css";

function Edition() {
  // a link like ".../editions/2026-10-01#article-x" can arrive as one param "2026-10-01#article-x" (the "#" is encoded
  // as %23 inside the HashRouter URL) — keep only the date and use the rest as the jump target
  const [date, paramHash = ""] = String(useParams().date || "").split("#");
  const location = useLocation();
  const jumpHash = location.hash ? decodeURIComponent(location.hash.slice(1)) : paramHash;
  const { siteContent } = useSiteContent();
  const c = siteContent.newspaperPage || {};
  const [data, setData] = useState({ items: [], news: [], pdfReady: false });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    document.title = `ฉบับวันที่ ${thaiDate(date)} | Maneenopkao Group`;
    setLoaded(false);
    getEdition(date)
      .then((d) => setData(d))
      .catch(() => setData({ items: [], news: [], pdfReady: false }))
      .finally(() => setLoaded(true));
  }, [date]);

  // jump to #notice-<id> from search results once the sheets are rendered
  useEffect(() => {
    if (!loaded || !jumpHash) return;
    document.getElementById(jumpHash)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [loaded, jumpHash]);

  const articles = data.articles || []; // admin news / PR → pages 2..
  const paperPdfs = data.paperPdfs || []; // newspaper PDFs the admin uploaded for this date ("ลงประกาศ")
  const items = data.items || [];       // customer notices → after the articles
  const news = (data.news || []).filter((n) => n && n.title);
  const dateText = thaiDate(date);
  // "ฉบับที่ 12 ปี 2569" — counted from the first edition with content (backend editionNumbers)
  // masthead / back cover wording (site content newspaperPaper; the API sends the same values the PDF uses)
  const paper = { ...(siteContent.newspaperPaper || {}), ...(data.paper || {}) };
  // page numbers measured on the PDF (a long article runs over several pages) — the same numbers as the download
  const layout = data.layout || null;
  const noticeFirstPage = layout?.noticeStart ?? 2 + articles.length;
  const articleStart = (i) => layout?.articleStarts?.[i] ?? i + 2;
  const articlePages = (i) => {
    const start = articleStart(i);
    const end = layout?.articleEnds?.[i] ?? start;
    return end > start ? `${start}–${end}` : String(start);
  };
  const jumpTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  // Customer notices: 1 A4 sheet = 4 notices (2×2), the same as the printed PDF. Each printable page of a notice
  // (an image, or one page of its PDF) takes one quarter — a normal notice is exactly one quarter.
  const slots = [];
  items.forEach((it, n) => {
    const parts = [];
    (it.files || []).forEach((f) => {
      if (/^image\//.test(f.type)) parts.push({ kind: "image", url: apiUrl(f.url) });
      else if (f.type === "application/pdf") {
        const pages = f.pages || 1;
        for (let p = 1; p <= pages; p++) parts.push({ kind: "pdf", url: apiUrl(f.url), page: p, pages });
      }
    });
    if (parts.length === 0) parts.push({ kind: "text" });
    parts.forEach((part, k) => slots.push({ ...part, item: it, no: articles.length + n + 1, first: k === 0, part: k + 1, parts: parts.length }));
  });
  const sheets = [];
  for (let i = 0; i < slots.length; i += 4) sheets.push(slots.slice(i, i + 4));
  const backPage = layout?.backPage ?? noticeFirstPage + sheets.length; // the back cover is the last page
  const noticePage = {};
  slots.forEach((s, i) => { if (s.first) noticePage[s.item.id] = layout?.noticePages?.[s.item.id] ?? noticeFirstPage + Math.floor(i / 4); });
  const pad2 = (n) => String(n).padStart(2, "0");

  return (
    <main id="main" className="edition-page">
      <div className="container edition-toolbar">
        <Link className="breadcrumb" to="/newspaper">← หนังสือพิมพ์</Link>
        <div>
          {/* the web page is open at once; the PDF downloads open the next day (owner rule, Edit1.pdf) */}
          {data.pdfReady && (
            <a className="button dark" href={editionPdfUrl(date)} target="_blank" rel="noreferrer">{c.editionDownload}</a>
          )}
          {paperPdfs.filter((p) => p.url).map((p) => (
            <a key={p.id} className="button outline" href={fileHref(p.url)} target="_blank" rel="noreferrer" title={p.title}>
              {paperPdfs.length > 1 ? p.title : "หนังสือพิมพ์ฉบับพิมพ์ (PDF)"} ↓
            </a>
          ))}
          {loaded && !data.pdfReady && (data.hasContent || paperPdfs.length > 0) && (
            <span className="download-lock-label">ดาวน์โหลด PDF ฉบับนี้ได้ตั้งแต่ {thaiDate(data.downloadFrom)}</span>
          )}
          {loaded && !data.hasContent && paperPdfs.length === 0 && (
            <span className="download-lock-label">{c.editionLocked || "ฉบับนี้ยังไม่มีไฟล์ให้ดาวน์โหลด"}</span>
          )}
        </div>
      </div>

      <div className="paper-book-v2 download-open">

        <div className="book-index">
          <p className="eyebrow">IN THIS EDITION / <span className="th">{articles.length + items.length} รายการ</span></p>
          <p className="book-page-guide">หน้า 1 ข่าวจากหน้าหลัก · หน้า 2 เป็นต้นไป ข่าวและประชาสัมพันธ์ ต่อด้วยประกาศลูกค้า</p>
          {articles.map((a, i) => (
            <a key={a.id} href={`#article-${a.id}`} onClick={(e) => { e.preventDefault(); jumpTo(`article-${a.id}`); }}>
              <span>{String(i + 1).padStart(2, "0")}</span>
              <span>{a.title}</span>
              <span>หน้า {articlePages(i)}</span>
            </a>
          ))}
          {items.map((it, i) => (
            <a key={it.id} href={`#notice-${it.id}`} onClick={(e) => { e.preventDefault(); jumpTo(`notice-${it.id}`); }}>
              <span>{String(articles.length + i + 1).padStart(2, "0")}</span>
              <span>{it.title}</span>
              <span>หน้า {noticePage[it.id]}</span>
            </a>
          ))}
        </div>

        <section className="paper-sheet paper-front">
          {/* masthead like the printed paper (and the PDF): QR · name · "ฉบับที่ N ปีที่ Y วันที่ … ราคา … บาท" · website */}
          <header className="paper-masthead">
            <div className="paper-masthead-top">
              <figure className="paper-masthead-qr">
                <img src={PaperQr} alt="QR code" />
                <figcaption>{paper.qrCaption}</figcaption>
              </figure>
              <h1>{paper.name || "มณีนพเก้า นิวส์กรุ๊ป"}</h1>
            </div>
            <div className="paper-masthead-bar">
              <span>
                {data.issueNo ? <>ฉบับที่ {data.issueNo}<i />ปีที่ {data.volume}<i /></> : null}
                วันที่ {data.dateLong || dateText}
                {paper.price ? <><i />ราคา {paper.price} บาท</> : null}
              </span>
              <strong>{paper.website}</strong>
            </div>
          </header>
          <div className="front-news-grid">
            {news.length === 0 && articles.length > 0 ? (
              // no front-page items in site content: lead with this edition's own news / PR articles
              articles.slice(0, 4).map((a, i) => (
                <article key={a.id}>
                  <span>{a.category || "MANEENOPKAO NEWS"}</span>
                  <h2>
                    <a href={`#article-${a.id}`} onClick={(e) => { e.preventDefault(); jumpTo(`article-${a.id}`); }}>{a.title}</a>
                  </h2>
                  {a.summary && <p>{plainText(a.summary)}</p>}
                  <small className="front-news-more">อ่านต่อหน้า {articleStart(i)}</small>
                </article>
              ))
            ) : news.length === 0 ? (
              <div className="front-news-empty">
                <span>{c.frontEmptyLabel}</span>
                <h2>{c.frontEmptyTitle}</h2>
                <p><Lines text={c.frontEmptyText} /></p>
              </div>
            ) : (
              news.map((n, i) => (
                <article key={i}>
                  <span>MANEENOPKAO NEWS</span>
                  <h2>{n.title}</h2>
                  {n.text && <div className="front-news-text"><RichText text={n.text} /></div>}
                </article>
              ))
            )}
          </div>
          <footer className="paper-page-footer">
            <span>มณีนพเก้า นิวส์กรุ๊ป</span>
            <span>{dateText} · หน้า 1</span>
          </footer>
        </section>

        {articles.map((a, i) => {
          return (
            <section key={a.id} id={`article-${a.id}`} className="paper-sheet">
              <div className="notice-page-heading">
                <strong>{a.category || "ข่าวสารองค์กร"}</strong>
                <span>{dateText} · หน้า {articlePages(i)}</span>
              </div>
              <article className="book-article" style={{ marginTop: 0, paddingTop: 0, borderTop: 0 }}>
                <p className="eyebrow">MANEENOPKAO NEWS</p>
                <h2>{a.title}</h2>
                <p className="article-meta">{dateText} · {a.author ? `เรียบเรียงโดย ${a.author}` : "มณีนพเก้า นิวส์กรุ๊ป"}</p>
                {a.summary && <p className="article-lead">{plainText(a.summary)}</p>}
                <div className="reading-body">
                  <RichText text={a.body} />
                </div>
                {(a.documents || []).map((d) => (
                  <a key={d.url} className="notice-cell-pdf article-pdf-link" href={apiUrl(d.url)} target="_blank" rel="noreferrer">
                    <strong>{d.name || "ไฟล์ PDF"}</strong>
                    <span>เปิดไฟล์ PDF ↗</span>
                  </a>
                ))}
                {(a.files || []).length > 0 && (
                  <div className="article-sheet-images">
                    {a.files.map((f) => <img key={f.url} src={apiUrl(f.url)} alt={a.title} />)}
                  </div>
                )}
                <ArticleCredit article={a} />
                <Link className="book-online" to={`/newspaper/articles/${a.id}`}>อ่านบนเว็บไซต์ ↗</Link>
              </article>
              <footer className="paper-page-footer">
                <span>มณีนพเก้า นิวส์กรุ๊ป</span>
                <span>{dateText} · หน้า {articlePages(i)}</span>
              </footer>
            </section>
          );
        })}

        {sheets.map((sheet, k) => (
          <section key={k} className="paper-sheet notice-sheet">
            <div className="notice-page-heading">
              <strong>ประกาศลูกค้า</strong>
              <span>{dateText} · หน้า {noticeFirstPage + k}</span>
            </div>
            <div className="notice-grid">
              {sheet.map((s) => (
                <article
                  key={`${s.item.id}-${s.part}`}
                  id={s.first ? `notice-${s.item.id}` : undefined}
                  className="notice-cell"
                >
                  <p className="notice-cell-caption">
                    ประกาศที่ {pad2(s.no)}{s.parts > 1 ? ` (${s.part}/${s.parts})` : ""} · {s.item.title}
                  </p>
                  <div className="notice-cell-body">
                    {s.kind === "image" && (
                      <a href={s.url} target="_blank" rel="noreferrer" title="เปิดภาพประกาศขนาดเต็ม">
                        <img className="notice-raster-preview" src={s.url} alt={s.item.title} loading="lazy" />
                      </a>
                    )}
                    {s.kind === "pdf" && (
                      <a className="notice-cell-pdf" href={s.url} target="_blank" rel="noreferrer">
                        <strong>ประกาศไฟล์ PDF{s.pages > 1 ? ` หน้า ${s.page}/${s.pages}` : ""}</strong>
                        <span>เปิดไฟล์ประกาศ ↗</span>
                      </a>
                    )}
                    {s.kind === "text" && <p className="column-copy">{s.item.summary || s.item.title}</p>}
                  </div>
                  {s.item.companyName && <p className="notice-cell-ref">{s.item.companyName}</p>}
                </article>
              ))}
            </div>
            <footer className="paper-page-footer">
              <span>มณีนพเก้า นิวส์กรุ๊ป</span>
              <span>{dateText} · หน้า {noticeFirstPage + k}</span>
            </footer>
          </section>
        ))}

        {loaded && items.length === 0 && articles.length === 0 && paperPdfs.length === 0 && <p className="no-notices">{c.noNotices}</p>}

        {loaded && (articles.length > 0 || items.length > 0) && (
          // back cover, the last page of every edition — the same as the PDF
          <section className="paper-sheet paper-back">
            <div className="paper-back-send">
              <div className="paper-back-logo">
                <strong>{paper.name}</strong>
                <small>{paper.website}</small>
              </div>
              <p>{paper.sendToLabel || "กรุณาส่ง"}</p>
            </div>
            <div className="paper-back-info">
              {paper.registrationNo && <p>{paper.bannerTitle} เลขทะเบียน {paper.registrationNo}</p>}
              {paper.issn && <p className="paper-back-issn">ISSN {paper.issn}</p>}
              <div className="paper-back-banner">
                <strong>{paper.bannerTitle}</strong>
                <span>{paper.bannerSubtitle}</span>
                <small>{paper.bannerContact}</small>
              </div>
              {paper.editorLine && <p className="paper-back-editor">{paper.editorLine}</p>}
              {paper.officeLine && <p className="paper-back-office">{paper.officeLine}</p>}
            </div>
            <footer className="paper-page-footer">
              <span>มณีนพเก้า นิวส์กรุ๊ป</span>
              <span>{dateText} · หน้า {backPage}</span>
            </footer>
          </section>
        )}
      </div>
    </main>
  );
}

export default Edition;
